// ACC.A3 (#413) — Code Review Report card with two-section split.
//
// Renders the Phase 5 code_review_report artefact's JSONB content as
// two sections — issues the agent resolved autonomously, and issues
// the engineer must address — plus a per-OPEN-issue Acknowledge button
// that drives the gate-passing flow.
//
// Backwards-compat: pre-A3 reports (no classification / status /
// resolution_attempts on issues) are rendered with every issue under
// "Requires your attention" and an Acknowledge button on each. New and
// legacy issues take the same code path; the only difference is the
// optional iteration / classification badges that surface when the A3
// fields are present.

import { useMemo, useState } from 'react';
import { CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

export interface ResolutionAttempt {
  attemptN:       number;
  fixText:        string;
  selfEvaluation: 'CONFIDENT' | 'UNCERTAIN' | 'NEEDS_HUMAN';
  evaluatedAt:    string;
}

export interface CodeReviewIssue {
  severity:    'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  file:        string;
  lineRange:   string;
  description: string;
  suggestedFix?:        string;
  // ACC.A3 additions — all optional for legacy report compat.
  classification?:      'AUTO_RESOLVABLE' | 'REQUIRES_HUMAN';
  status?:              'RESOLVED_AUTONOMOUSLY' | 'OPEN';
  resolution_attempts?: ResolutionAttempt[];
  acknowledged?:        { at: string; by: string };
}

export interface ADRComplianceItem {
  adrTitle: string;
  status:   'COMPLIANT' | 'VIOLATION' | 'NOT_APPLICABLE';
  notes?:   string;
}

/** CRA.A3 (#947) — per-report re-run tracking. count = number of
 *  user-triggered Re-run invocations; history records who and when.
 *  Absent or `count: 0` means no re-runs yet. */
export interface ReportReruns {
  count:   number;
  history: Array<{ triggeredAt: string; triggeredBy: string }>;
}

export interface CodeReviewReport {
  summary:           string;
  verdict:           'APPROVE' | 'REQUEST_CHANGES' | 'COMMENT';
  confidenceScore:   number;
  issues:            CodeReviewIssue[];
  adrCompliance:     ADRComplianceItem[];
  positiveObservations:  string[];
  suggestedImprovements: string[];
  summaryStats: {
    critical: number; high: number; medium: number; low: number;
    filesReviewed: number;
  };
  // CRA.A3 — re-run state. Optional for backwards-compat with reports
  // persisted before this PR.
  reruns?: ReportReruns;
}

/** Server-side cap mirror. Must match RERUN_CAP in
 *  apps/api/src/routes/artefacts.ts. UI uses this for the disabled
 *  state; the server is authoritative (returns 429 if bypassed). */
const RERUN_CAP = 3;

interface CodeReviewReportCardProps {
  report:     CodeReviewReport;
  artefactId: string;
  // SEC.I4 C1 (Option D): the artefact's approval status. When 'approved' and this
  // is the LAST unacknowledged issue, the Acknowledge button makes the merge-
  // completing consequence EXPLICIT — the human must know that this click is the
  // one that merges the PR (the merge is no longer silent on a model decision; it
  // must not become silent on the human's last ack either).
  artefactStatus?: string;
}

const VERDICT_STYLE: Record<string, { bg: string; color: string; label: string }> = {
  APPROVE:         { bg: '#059669', color: 'white', label: 'Approve' },
  REQUEST_CHANGES: { bg: '#dc2626', color: 'white', label: 'Request Changes' },
  COMMENT:         { bg: '#d97706', color: 'white', label: 'Comment' },
};

const SEVERITY_INDICATOR: Record<string, { icon: string; color: string }> = {
  CRITICAL: { icon: '\u{1F534}', color: '#dc2626' },
  HIGH:     { icon: '\u{1F7E0}', color: '#ea580c' },
  MEDIUM:   { icon: '\u{1F7E1}', color: '#d97706' },
  LOW:      { icon: '⚪',    color: '#6b7280' },
};

/** Effective status — legacy issues with no `status` field default OPEN. */
function effectiveStatus(issue: CodeReviewIssue): 'RESOLVED_AUTONOMOUSLY' | 'OPEN' {
  return issue.status === 'RESOLVED_AUTONOMOUSLY' ? 'RESOLVED_AUTONOMOUSLY' : 'OPEN';
}

function formatAcknowledgedDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

export default function CodeReviewReportCard({ report, artefactId, artefactStatus }: CodeReviewReportCardProps) {
  const navigate = useNavigate();
  const { user } = useAuth();

  // CRA.A3 (#947) — local report state. Initialised from the prop;
  // mutated by re-run responses (the endpoint returns the updated
  // report so we don't need a second GET). All renders read from
  // localReport instead of the prop so re-run results show up
  // immediately. The prop is only used for the initial seed; if the
  // parent re-renders with a fresher report, useEffect could sync —
  // out of scope for this PR (parents currently don't re-fetch
  // mid-mount).
  const [localReport, setLocalReport] = useState<CodeReviewReport>(report);

  const verdict = VERDICT_STYLE[localReport.verdict] ?? VERDICT_STYLE.COMMENT;
  const stats = localReport.summaryStats;
  const adrTotal = localReport.adrCompliance.length;
  const adrCompliant = localReport.adrCompliance.filter(a => a.status === 'COMPLIANT').length;
  const adrViolations = localReport.adrCompliance.filter(a => a.status === 'VIOLATION').length;

  // Local optimistic acknowledge map — index → ack stamp. Merged with the
  // server-provided issue.acknowledged at render time so the click-then-
  // network-roundtrip flow flips the UI immediately. Persists across
  // re-renders within this component instance; a hard reload reads the
  // latest version from the API and rebuilds.
  const [optimisticAcks, setOptimisticAcks] = useState<Record<number, { at: string; by: string }>>({});
  const [ackPending, setAckPending] = useState<Record<number, boolean>>({});

  // SEC.I4 C1 (Option D) — PERSISTENT merge-completed notice. Set when an ack's
  // response reports `mergeCompleted` (i.e. that ack was the final one and the PR
  // merged). Rendered as a persistent banner (NOT a transient toast) so a human in
  // a stale multi-tab race — who may have clicked a plain "Acknowledge" without the
  // pre-click affordance — is still un-missably told their ack merged the PR.
  const [mergeCompletedNotice, setMergeCompletedNotice] = useState<{ prUrl: string | null } | null>(null);

  // CRA.A3 — re-run state.
  const [rerunPending, setRerunPending] = useState(false);
  const [rerunError, setRerunError] = useState<string | null>(null);

  const issues = localReport.issues ?? [];
  const rerunCount = localReport.reruns?.count ?? 0;
  const rerunCapReached = rerunCount >= RERUN_CAP;

  const resolved = useMemo(
    () => issues.map((issue, i) => ({ issue, i })).filter(({ issue }) => effectiveStatus(issue) === 'RESOLVED_AUTONOMOUSLY'),
    [issues],
  );
  const open = useMemo(
    () => issues.map((issue, i) => ({ issue, i })).filter(({ issue }) => effectiveStatus(issue) === 'OPEN'),
    [issues],
  );

  const totalAttempts = useMemo(
    () => issues.reduce((s, i) => s + (i.resolution_attempts?.length ?? 0), 0),
    [issues],
  );

  const handleAcknowledge = async (issueIdx: number) => {
    if (ackPending[issueIdx]) return;
    if (optimisticAcks[issueIdx] || issues[issueIdx]?.acknowledged?.at) return;
    setAckPending(prev => ({ ...prev, [issueIdx]: true }));
    // Optimistic stamp uses local clock; the server's stamp may differ
    // by a few ms but the UI doesn't render the ISO directly — the
    // formatted date is the same to a day's resolution.
    const optimisticStamp = { at: new Date().toISOString(), by: user?.id ?? '' };
    setOptimisticAcks(prev => ({ ...prev, [issueIdx]: optimisticStamp }));
    try {
      const result = await apiFetch<{
        artefactId:   string;
        issueIdx:     number;
        acknowledged: { at: string; by: string };
        mergeCompleted?: boolean;
        prUrl?:          string | null;
      }>(`/artefacts/${artefactId}/issues/${issueIdx}/acknowledge`, { method: 'PATCH' });
      // Replace the optimistic stamp with the server's authoritative one.
      setOptimisticAcks(prev => ({ ...prev, [issueIdx]: result.acknowledged }));
      // SEC.I4 C1 (Option D) — if THIS ack completed the PR merge, raise the
      // persistent consequence-notice (the always-informed backstop for the stale
      // multi-tab path; the pre-click affordance covers the fresh-load path).
      if (result.mergeCompleted) {
        setMergeCompletedNotice({ prUrl: result.prUrl ?? null });
      }
    } catch (err) {
      // Rollback the optimistic stamp on failure so the button reappears.
      console.error('[CodeReviewReportCard] acknowledge failed:', (err as Error).message);
      setOptimisticAcks(prev => {
        const next = { ...prev };
        delete next[issueIdx];
        return next;
      });
    } finally {
      setAckPending(prev => {
        const next = { ...prev };
        delete next[issueIdx];
        return next;
      });
    }
  };

  // CRA.A3 (#947) — re-run handler. POSTs to the rerun endpoint, swaps
  // the local report on success. No optimistic UI: the call can take
  // many seconds (up to 3 × N Haiku calls) so a loading state is the
  // honest UX. On 429 (cap) or 409 (governed-mode flip), surface the
  // server's message; the button itself will hide on next render since
  // rerunCount will have advanced or the project mode is now governed
  // (the user would need to refresh to re-fetch the latest report
  // shape — out of scope for V1).
  const handleRerun = async () => {
    if (rerunPending) return;
    if (rerunCapReached) return;
    setRerunPending(true);
    setRerunError(null);
    try {
      const result = await apiFetch<{
        artefactId:         string;
        runNumber:          number;
        openIssueCount:     number;
        totalAttemptsAfter: number;
        capRemaining:       number;
        report:             CodeReviewReport;
      }>(`/artefacts/${artefactId}/rerun`, { method: 'POST' });
      setLocalReport(result.report);
      // Reset optimistic acks since the new report includes server-side
      // ack stamps already; rendering them twice would confuse the UI.
      setOptimisticAcks({});
    } catch (err) {
      const msg = (err as Error).message || 'Re-run failed';
      console.error('[CodeReviewReportCard] re-run failed:', msg);
      setRerunError(msg);
    } finally {
      setRerunPending(false);
    }
  };

  const cardStyle: React.CSSProperties = {
    background:   'var(--surface)',
    border:       '1px solid var(--border)',
    borderRadius: 'var(--radius-lg)',
    padding:      20,
    marginTop:    12,
  };

  const ackedFor = (idx: number): { at: string; by: string } | null => {
    return optimisticAcks[idx] ?? issues[idx]?.acknowledged ?? null;
  };

  // SEC.I4 C1 (Option D) — when the report is approved and exactly ONE issue
  // remains unacknowledged, acking it completes the PR merge (the handoff's
  // ack-completion path fires on the final ack). Make that explicit on the button
  // so the human knows this click is the one that merges — the merge is no longer
  // silent on a model decision and must not become silent on the last ack.
  //
  // Count by CONFIRMED acks only: an issue is "still unacknowledged" if it is not
  // acked OR its ack is in-flight (ackPending). Using the optimistic stamp here
  // would momentarily mislabel another issue as the merge-completing one while a
  // prior ack is still round-tripping (and could roll back on error).
  const unacknowledgedCount = issues.filter((_, i) => !ackedFor(i) || !!ackPending[i]).length;
  const lastAckCompletesMerge = artefactStatus === 'approved' && unacknowledgedCount === 1;

  const renderIssueCard = (issue: CodeReviewIssue, idx: number) => {
    const status = effectiveStatus(issue);
    const ind = SEVERITY_INDICATOR[issue.severity] ?? SEVERITY_INDICATOR.LOW;
    const ack = ackedFor(idx);
    const attempts = issue.resolution_attempts?.length ?? 0;
    return (
      <div
        key={idx}
        data-testid={`issue-card-${idx}`}
        data-issue-status={status}
        style={{
          border:       '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)',
          padding:      '10px 12px',
          marginBottom: 8,
          background:   status === 'RESOLVED_AUTONOMOUSLY' ? 'rgba(16,185,129,0.04)' : 'transparent',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
          <span style={{ color: ind.color, fontSize: 13 }}>{ind.icon}</span>
          <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>
            {issue.file}:{issue.lineRange}
          </span>
          {status === 'RESOLVED_AUTONOMOUSLY' && attempts > 0 && (
            <span
              data-testid={`iteration-badge-${idx}`}
              style={{
                fontFamily:   'var(--font-body)',
                fontSize:     11,
                fontWeight:   600,
                padding:      '1px 7px',
                borderRadius: 'var(--radius-sm)',
                background:   'rgba(16,185,129,0.12)',
                color:        '#065f46',
                border:       '1px solid rgba(16,185,129,0.3)',
              }}
            >
              {attempts} attempt{attempts !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text)', marginTop: 4, marginBottom: issue.suggestedFix ? 4 : 0 }}>
          {issue.description}
        </p>
        {issue.suggestedFix && (
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)', marginTop: 2, fontStyle: 'italic' }}>
            Fix: {issue.suggestedFix}
          </p>
        )}
        {/* SEC.I4 C1 (Option D) — surface the agent's pre-drafted resolution on
            auto-resolved issues so the human reviews it before acknowledging. */}
        {status === 'RESOLVED_AUTONOMOUSLY' && attempts > 0 && issue.resolution_attempts?.[attempts - 1]?.fixText && (
          <p data-testid={`suggested-resolution-${idx}`} style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: '#065f46', marginTop: 4 }}>
            Suggested resolution: {issue.resolution_attempts[attempts - 1].fixText}
          </p>
        )}
        {/* SEC.I4 C1 (Option D) — every unacknowledged issue (OPEN or
            agent-suggested/RESOLVED_AUTONOMOUSLY) shows the Acknowledge
            affordance; the human `acknowledged` stamp is the single accept
            signal. RESOLVED_AUTONOMOUSLY no longer means "silently done". */}
        {ack ? (
            <p
              data-testid={`acknowledged-banner-${idx}`}
              style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)', marginTop: 8, fontStyle: 'italic' }}
            >
              Acknowledged by {ack.by === user?.id ? (user?.name ?? 'you') : ack.by} on {formatAcknowledgedDate(ack.at)}
            </p>
          ) : (
            <button
              data-testid={`acknowledge-btn-${idx}`}
              onClick={() => void handleAcknowledge(idx)}
              disabled={!!ackPending[idx]}
              style={{
                marginTop:     8,
                display:       'inline-flex',
                alignItems:    'center',
                gap:           6,
                padding:       '4px 10px',
                fontFamily:    'var(--font-body)',
                fontSize:      12,
                fontWeight:    600,
                color:         'var(--primary)',
                background:    'transparent',
                border:        '1px solid var(--primary)',
                borderRadius:  'var(--radius-sm)',
                cursor:        ackPending[idx] ? 'wait' : 'pointer',
                transition:    'all 150ms',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(124,58,237,0.08)'; e.currentTarget.style.color = '#7C3AED'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--primary)'; }}
            >
              <CheckCircle size={12} />
              {ackPending[idx]
                ? 'Acknowledging…'
                : lastAckCompletesMerge ? 'Acknowledge & complete PR merge' : 'Acknowledge'}
            </button>
          )
        }
        {!ack && lastAckCompletesMerge && (
          <p data-testid={`completes-merge-note-${idx}`} style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: '#b45309', marginTop: 4 }}>
            This is the last unacknowledged issue — acknowledging it completes the PR merge.
          </p>
        )}
      </div>
    );
  };

  return (
    <div style={cardStyle}>
      {/* SEC.I4 C1 (Option D) — PERSISTENT merge-completed notice. Stays until the
          card unmounts/refetches (not a transient toast), so a human in a stale
          multi-tab race is un-missably told their acknowledgment merged the PR. */}
      {mergeCompletedNotice && (
        <div
          data-testid="merge-completed-notice"
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 14px', marginBottom: 16,
            background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.35)',
            borderRadius: 'var(--radius-sm)',
            fontFamily: 'var(--font-body)', fontSize: 13, color: '#065f46',
          }}
        >
          <CheckCircle size={14} />
          <span>Your acknowledgment completed the review — the PR has been merged.</span>
          {mergeCompletedNotice.prUrl && (
            <a
              href={mergeCompletedNotice.prUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--primary)', fontWeight: 600, textDecoration: 'none' }}
            >
              View PR ↗
            </a>
          )}
        </div>
      )}
      {/* Header: verdict badge + confidence + total attempts badge */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <span style={{
          display:      'inline-block',
          padding:      '4px 14px',
          borderRadius: 20,
          fontSize:     13,
          fontWeight:   700,
          fontFamily:   'var(--font-display)',
          background:   verdict.bg,
          color:        verdict.color,
        }}>
          {verdict.label}
        </span>
        <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>
          Confidence: {Math.round(localReport.confidenceScore * 100)}%
        </span>
        {totalAttempts > 0 && (
          <span
            data-testid="total-attempts-badge"
            style={{
              fontFamily:   'var(--font-body)',
              fontSize:     11,
              fontWeight:   600,
              padding:      '2px 8px',
              borderRadius: 'var(--radius-sm)',
              background:   'rgba(124,58,237,0.10)',
              color:        'var(--primary)',
              border:       '1px solid rgba(124,58,237,0.25)',
            }}
          >
            {totalAttempts} auto-resolve attempt{totalAttempts !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Summary */}
      <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', marginBottom: 16, lineHeight: 1.5 }}>
        {localReport.summary}
      </p>

      {/* Issue counts */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
        {(['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'] as const).map(sev => {
          const count = stats[sev.toLowerCase() as keyof typeof stats] as number;
          const ind = SEVERITY_INDICATOR[sev];
          return (
            <span key={sev} style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: ind.color }}>
              {ind.icon} {count} {sev.toLowerCase()}
            </span>
          );
        })}
        <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>
          {stats.filesReviewed} files reviewed
        </span>
      </div>

      {/* ADR compliance */}
      {adrTotal > 0 && (
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: adrViolations > 0 ? '#dc2626' : '#059669', marginBottom: 12 }}>
          {adrViolations > 0
            ? `${adrViolations} ADR violation${adrViolations > 1 ? 's' : ''} found`
            : `${adrCompliant}/${adrTotal} ADRs compliant`}
        </p>
      )}

      {/* Agent-suggested-resolutions section. Hidden entirely when zero so
          legacy reports don't render an empty heading. SEC.I4 C1 (Option D):
          these are NOT silently done — each shows the agent's pre-drafted fix
          and requires a human acknowledgment (rendered by renderIssueCard). */}
      {resolved.length > 0 && (
        <div data-testid="section-resolved" style={{ marginBottom: 16 }}>
          <p style={{ fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600, color: '#065f46', marginBottom: 8 }}>
            Agent-suggested resolutions ({resolved.length}) — acknowledge to accept
          </p>
          {resolved.map(({ issue, i }) => renderIssueCard(issue, i))}
        </div>
      )}

      {/* Requires-your-attention section. Always renders the heading
          when at least one OPEN issue exists, so legacy reports surface
          here unchanged. */}
      {open.length > 0 && (
        <div data-testid="section-open" style={{ marginBottom: 12 }}>
          <p style={{ fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600, color: '#dc2626', marginBottom: 8 }}>
            Requires your attention ({open.length})
          </p>
          {open.map(({ issue, i }) => renderIssueCard(issue, i))}
        </div>
      )}

      {/* Action button row — View Full Report + (CRA.A3) Re-run review */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          onClick={() => navigate(`/artefacts?tabs=${artefactId}&active=${artefactId}`)}
          style={{
            padding:      '8px 16px',
            fontFamily:   'var(--font-display)',
            fontSize:     13,
            fontWeight:   600,
            color:        'var(--primary)',
            background:   'transparent',
            border:       '1px solid var(--primary)',
            borderRadius: 'var(--radius-sm)',
            cursor:       'pointer',
            transition:   'all 150ms',
          }}
        >
          View Full Report
        </button>

        {/* CRA.A3 (#947) — Re-run review button. Surfaces only when the
            verdict is REQUEST_CHANGES (other verdicts have nothing
            useful to re-run). Disabled at the per-report cap;
            disabled + spinner during the in-flight call. Server is
            authoritative (returns 429 if cap is bypassed).
            SEC.I4 C1 (Option D) — never on an APPROVED report: re-running the
            auto-resolve loop after approval is post-merge-flow iteration the
            engineer shouldn't trigger here; the report's `verdict` can be a stale
            REQUEST_CHANGES even after the human approved + acknowledged. */}
        {localReport.verdict === 'REQUEST_CHANGES' && artefactStatus !== 'approved' && (
          <button
            data-testid="rerun-btn"
            onClick={() => void handleRerun()}
            disabled={rerunPending || rerunCapReached}
            title={rerunCapReached
              ? `Maximum re-runs reached (${RERUN_CAP}). Acknowledge remaining issues or escalate.`
              : `Re-run the auto-resolve loop on currently-open issues (${RERUN_CAP - rerunCount} re-run${RERUN_CAP - rerunCount !== 1 ? 's' : ''} remaining).`}
            style={{
              padding:      '8px 16px',
              fontFamily:   'var(--font-display)',
              fontSize:     13,
              fontWeight:   600,
              color:        rerunCapReached || rerunPending ? 'var(--text-secondary)' : 'var(--primary)',
              background:   'transparent',
              border:       `1px solid ${rerunCapReached || rerunPending ? 'var(--border)' : 'var(--primary)'}`,
              borderRadius: 'var(--radius-sm)',
              cursor:       rerunPending ? 'wait' : (rerunCapReached ? 'not-allowed' : 'pointer'),
              transition:   'all 150ms',
            }}
          >
            {rerunPending ? 'Re-running…' : (rerunCapReached ? `Re-run unavailable (${RERUN_CAP}/${RERUN_CAP})` : `Re-run review (${rerunCount}/${RERUN_CAP})`)}
          </button>
        )}

        {/* Cap-reached helper text — surfaces alongside the disabled
            button so the reason is obvious without hovering. */}
        {localReport.verdict === 'REQUEST_CHANGES' && rerunCapReached && (
          <p
            data-testid="rerun-cap-helper"
            style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}
          >
            Maximum re-runs reached. Acknowledge remaining issues or escalate.
          </p>
        )}

        {/* Error message — surfaces on 4xx/5xx from the rerun endpoint
            (e.g. 409 DELIVERY_MODE_CONFLICT, 429 cap, network failure). */}
        {rerunError && (
          <p
            data-testid="rerun-error"
            style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: '#dc2626', margin: 0 }}
          >
            {rerunError}
          </p>
        )}
      </div>
    </div>
  );
}
